<template>
  <div>
    <h3 class="title mb-4">Tenant Information</h3>
    
    <div v-if="tenantsData.length === 0" class="text-center py-8">
      <v-icon size="64" color="grey lighten-1">mdi-account-plus</v-icon>
      <p class="text-subtitle-1 mt-4">No tenants added yet</p>
      <v-btn
        color="primary"
        @click="addTenant"
      >
        Add First Tenant
      </v-btn>
    </div>
    
    <div v-else>
      <!-- List of tenants -->
      <v-card
        v-for="(tenant, index) in tenantsData"
        :key="index"
        class="mb-4 rounded-md"
        outlined
      >
        <v-card-title class="secondary white--text pa-3">
          <v-icon class="mr-2" color="white" small>mdi-account</v-icon>
          <span class="subtitle-2 white--text font-weight-light">Tenant {{ index + 1 }}</span>
          <v-chip
            v-if="index === 0 || (tenant.newTenant && tenant.newTenant.isHeadTenant)"
            color="white"
            text-color="secondary"
            small
            class="ml-2"
          >
            Head Tenant
          </v-chip>
          <v-spacer />
          <v-btn
            icon
            color="white"
            @click="removeTenant(index)"
          >
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-text>
          <!-- Tenant existence check -->
          <v-radio-group
            v-model="tenant.isExisting"
            row
            class="mb-4"
          >
            <template v-slot:label>
              <div class="text-subtitle-2 black--text">
                Does this tenant already exist in agentPay?
              </div>
            </template>
            <v-radio label="Yes" :value="true"></v-radio>
            <v-radio label="No" :value="false"></v-radio>
          </v-radio-group>
          
          <!-- Existing tenant search -->
          <div v-if="tenant.isExisting === true" class="mb-4">
            <autocomplete-search
              v-model="tenant.existingTenant"
              :search-function="searchFunction"
              label="Search for existing tenant"
              placeholder="Enter at least 5 characters to search..."
              :rules="[rules.required]"
            />
            
            <v-card v-if="tenant.existingTenant" class="mt-4" outlined>
              <v-card-text>
                <div class="text-subtitle-2 mb-2">Selected Tenant:</div>
                <div>{{ tenant.existingTenant.Subject }}</div>
                <div class="text-caption">ID: {{ tenant.existingTenant.OID }}</div>
              </v-card-text>
            </v-card>
          </div>
          
          <!-- New tenant form -->
          <div v-else-if="tenant.isExisting === false">
            <v-row>
              <v-col cols="12" md="2">
                <v-select
                  v-model="tenant.newTenant.title"
                  :items="titles"
                  label="Title"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="tenant.newTenant.forename"
                  label="Forename"
                  outlined
                  dense
                  :rules="[rules.required]"
                  required
                />
              </v-col>
              <v-col cols="12" md="2">
                <v-text-field
                  v-model="tenant.newTenant.middleName"
                  label="Middle Name"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="tenant.newTenant.surname"
                  label="Surname"
                  outlined
                  dense
                  :rules="[rules.required]"
                  required
                />
              </v-col>
            </v-row>
            
            <v-row>
              <v-col cols="12" md="6">
                <v-menu
                  v-model="tenant.dateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="tenant.newTenant.dateOfBirth"
                      label="Date of Birth"
                      prepend-icon="mdi-calendar"
                      readonly
                      outlined
                      dense
                      v-bind="attrs"
                      v-on="on"
                      :rules="[rules.required]"
                    />
                  </template>
                  <v-date-picker
                    v-model="tenant.newTenant.dateOfBirth"
                    @input="tenant.dateMenu = false"
                    :max="maxDate"
                  />
                </v-menu>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="tenant.newTenant.emailAddress"
                  label="Email Address"
                  outlined
                  dense
                  :rules="[rules.required, rules.email]"
                  required
                />
              </v-col>
            </v-row>
            
            <v-row>
              <v-col cols="12" md="6">
                <v-checkbox
                  v-model="tenant.newTenant.emailLetters"
                  label="Send letters by email"
                  hide-details
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-checkbox
                  v-model="tenant.newTenant.sendTenantAppInvite"
                  label="Send tenant app invite"
                  hide-details
                />
              </v-col>
            </v-row>
            
            <v-row v-if="index > 0">
              <v-col cols="12">
                <v-checkbox
                  v-model="tenant.newTenant.isHeadTenant"
                  label="Set as head tenant"
                  hide-details
                />
              </v-col>
            </v-row>
          </div>
        </v-card-text>
      </v-card>
      
      <!-- Add another tenant button -->
      <v-btn
        color="primary"
        outlined
        @click="addTenant"
        class="mb-4"
      >
        <v-icon left>mdi-plus</v-icon>
        Add Another Tenant
      </v-btn>
    </div>
    
    <!-- Navigation buttons -->
    <v-row class="mt-6">
      <v-col cols="6">
        <v-btn
          text
          @click="$emit('back')"
        >
          <v-icon left>mdi-arrow-left</v-icon>
          Back
        </v-btn>
      </v-col>
      <v-col cols="6" class="text-right">
        <v-btn
          color="primary"
          :disabled="!canProceed"
          @click="proceed"
        >
          Next
          <v-icon right>mdi-arrow-right</v-icon>
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import AutocompleteSearch from '@/components/Tenancies/AutocompleteSearch.vue'

export default {
  name: 'TenantsStep',
  
  components: {
    AutocompleteSearch
  },
  
  props: {
    value: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    searchFunction: {
      type: Function,
      required: true
    }
  },
  
  data() {
    return {
      tenantsData: this.value,
      titles: ['Mr', 'Mrs', 'Miss', 'Ms', 'Dr', 'Prof'],
      rules: {
        required: v => !!v || 'This field is required',
        email: v => {
          if (!v) return true
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
          return emailRegex.test(v) || 'Please enter a valid email address'
        }
      }
    }
  },
  
  computed: {
    maxDate() {
      const eighteenYearsAgo = new Date()
      eighteenYearsAgo.setFullYear(eighteenYearsAgo.getFullYear() - 18)
      return eighteenYearsAgo.toISOString().substr(0, 10)
    },
    
    canProceed() {
      if (this.tenantsData.length === 0) {
        return false
      }
      
      return this.tenantsData.every(tenant => {
        if (tenant.isExisting === null) {
          return false
        }
        
        if (tenant.isExisting) {
          return !!tenant.existingTenant
        } else {
          return tenant.newTenant.forename && 
                 tenant.newTenant.surname &&
                 tenant.newTenant.dateOfBirth &&
                 tenant.newTenant.emailAddress
        }
      })
    }
  },
  
  watch: {
    value: {
      deep: true,
      handler(newVal) {
        this.tenantsData = newVal
      }
    },
    tenantsData: {
      deep: true,
      handler(val) {
        this.$emit('input', val)
      }
    }
  },
  
  methods: {
    addTenant() {
      const isFirstTenant = this.tenantsData.length === 0
      
      this.tenantsData.push({
        isExisting: null,
        existingTenant: null,
        newTenant: {
          title: '',
          forename: '',
          middleName: '',
          surname: '',
          dateOfBirth: null,
          emailAddress: '',
          emailLetters: true,
          sendTenantAppInvite: true,
          isHeadTenant: isFirstTenant
        },
        dateMenu: false
      })
    },
    
    removeTenant(index) {
      this.tenantsData.splice(index, 1)
      
      // Ensure first tenant is always head tenant
      if (this.tenantsData.length > 0 && index === 0) {
        if (this.tenantsData[0].newTenant) {
          this.tenantsData[0].newTenant.isHeadTenant = true
        }
      }
    },
    
    proceed() {
      if (this.canProceed) {
        this.$emit('next')
      }
    }
  }
}
</script>